import { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

import { Dropdown, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisV,
  faEye,
  faTimes,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Layout/DashboardLayout";

import CustomTable from "./../../Components/CustomTable";
import { placeholderImage } from "../../Assets/images";

import { questionModal, successModal } from "../../Components/CustomModal";
import CustomFilters from "../../Components/CustomFilters";
import CustomPagination from "../../Components/CustomPagination";
import CustomLoader from "../../Components/CustomLoader";

import { fetchData } from "../../Services/Sound";
import "./style.css";
import BASEURL from "../../Config/global";
import { PermissionsContext } from "../../Layout/PermissionContext";
import { disabledButtonByPermission } from "../../Components/Permissions";
import { deleteRequest, postRequest } from "../../Util/requests";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { listSorted } from "../../Services/Sorting";
import { getQuestionModalTitleForSorting, sortingUpdatedSuccessfullyMsg } from "../../Util/common";
import { translateEnglishToSpanish, translateFromSpanish } from "../../Util/translator";
import ReadOnlyDropdown from "../../Components/ReadOnlyDropdown";

const SoundManagement = () => {
  const { permissions } = useContext(PermissionsContext)
  const navigate = useNavigate();
  const sortRef = useRef(null);

  const [data, setData] = useState([]);
  const [isListPositionUpdated, setIsListPositionUpdated] = useState(false);
  const [enteries, setEnteries] = useState(10);
  const [offset, setOffset] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterSearchValue, setFilterSearchValue] = useState("");
  const [listDropdownValue, setListDropdownValue] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [updateData, setUpdateData] = useState(false);
  const [swapId, setSwapId] = useState(null);
  const [swapRecordId, setSwapRecordId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState({ error: true, text: '' });

  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("english");

  useEffect(() => {
    if (currentPage != undefined) {
      setOffset(currentPage);
    }
  }, [currentPage])

  useEffect(() => {
    document.title = "Relax Scofa | Sound Management";
  }, []);

  useEffect(() => {

    (async function () {
      try {
        // setLoader(true);
        const response = await fetchData(
          enteries,
          offset,
          dateFrom,
          dateTo,
          filterSearchValue,
          listDropdownValue,
          selectedLanguage
        );
        // console.log(response.data.data);
        setData(response.data.data);
        setTotalCount(response.data.count);
        setLoader(false);
      } catch (error) {
        console.error(error);
      }
    })();

  }, [enteries, filterSearchValue, dateTo, updateData, offset, listDropdownValue, selectedLanguage]);

  const deleteSoundAction = (id, name) => {
    questionModal
      .fire({
        title: `Do you want to hide ${name} Sound?`,
        confirmButtonText: "Yes",
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteSound(id, name);
        }
      });
  };

  const deleteSound = async (id, name) => {
    setLoader(true);
    try {
      const response = await deleteRequest(`/api/sounds/${id}`);
      console.log(response.data);
      if (response.data.error === false) {
        successModal.fire({
          text: `${name} Sound has been hidden`,
          confirmButtonText: "Continue",
        });
        setUpdateData(!updateData);
        setLoader(false);
      }
    } catch (error) {
      console.log("error =======>   ", error);
      setUpdateData(!updateData);
      setLoader(false);
    }
  };

  const handleDuplicateSound = async (item) => {
    // Determine target language
    const currentLanguage = item.language || 'english';
    const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';
    const targetLanguageText = targetLanguage === 'english' ? 'English' : 'Spanish';
    
    questionModal
      .fire({
        title: `Do you want to duplicate this sound into ${targetLanguageText.toLowerCase()}?`,
        confirmButtonText: `Duplicate into ${targetLanguageText}`,
      })
      .then((result) => {
        if (result.isConfirmed) {
          duplicateSound(item);
        }
      });
  };

  const duplicateSound = async (item) => {
    setLoader(true);
    try {
      // Fetch full sound data
      const response = await axios.get(`${BASEURL}/api/sounds/${item.id}`);
      const soundData = response.data.data[0];
      
      if (!soundData) {
        setLoader(false);
        return;
      }

      // Determine current language and target language
      const currentLanguage = soundData.language || 'english';
      const targetLanguage = currentLanguage === 'english' ? 'spanish' : 'english';
      
      // Translate title
      let translatedTitle = soundData.title;
      if (soundData.title) {
        let translationResult;
        if (currentLanguage === 'english') {
          translationResult = await translateEnglishToSpanish(soundData.title);
        } else {
          translationResult = await translateFromSpanish(soundData.title);
        }
        
        if (translationResult.success) {
          translatedTitle = translationResult.translatedText;
        } else {
          setLoader(false);
          successModal.fire({
            text: `Translation failed: ${translationResult.error}`,
            confirmButtonText: "Continue",
          });
          return;
        }
      }

      // Create FormData for new sound
      const formDataToSend = new FormData();
      formDataToSend.append("image", soundData.image);
      formDataToSend.append("thumbnail", soundData.thumbnail);
      formDataToSend.append("ios_sound", soundData.ios_sound);
      formDataToSend.append("audio", soundData.audio);
      formDataToSend.append("ios_patch", soundData.ios_patch);
      formDataToSend.append("audio_mobile", soundData.audio_mobile);
    
      formDataToSend.append("title", translatedTitle);
      formDataToSend.append("premium", soundData.premium || false);
      
      if (soundData.subcategory_obj?.child_subcategory_id) {
        formDataToSend.append("soundcategory", soundData.subcategory_obj.child_subcategory_id);
      } else if (soundData.soundcategory) {
        formDataToSend.append("soundcategory", soundData.soundcategory);
      }
      
      formDataToSend.append("featured", soundData.featured || false);
      formDataToSend.append("naration", false);
      formDataToSend.append("child_content", soundData.child_content || false);
      formDataToSend.append("language", targetLanguage);
      formDataToSend.append("duration", soundData?.duration);


      const createResponse = await postRequest(`/api/sounds/`, formDataToSend, null);
      
      if (createResponse.data.error === false) {
        successModal.fire({
          text: `Sound duplicated successfully in ${targetLanguage}!`,
          confirmButtonText: "Continue",
        });
        setUpdateData(!updateData);
        setLoader(false);
      } else {
        setLoader(false);
        successModal.fire({
          text: `Failed to duplicate sound: ${createResponse.data.message || 'Unknown error'}`,
          confirmButtonText: "Continue",
        });
      }
    } catch (error) {
      console.log("error =======>   ", error);
      setLoader(false);
      successModal.fire({
        text: `An error occurred while duplicating sound: ${error.message || 'Unknown error'}`,
        confirmButtonText: "Continue",
      });
    }
  };

  const soundHeader = [
    {
      key: "id",
      title: "S.No",
    },
    {
      key: "title",
      title: "Title",
    },
    {
      key: "type",
      title: "Type",
    },
    {
      key: "category",
      title: "Category",
    },
    {
      key: "subcategory",
      title: "Sub Category",
    },
    {
      key: "underagecontent",
      title: "Under age content",
    },
    {
      key: "actions",
      title: "Actions",
    },
  ];

  const swipModal = (id, recordId) => {
    setError({ error: false, text: '' })
    setSwapId(id);
    // setSwapRecordId(recordId);
    setShowModal(true);
  }

  const setSorting = async () => {
    setError({ error: false, text: '' });
    let recordId = parseInt(sortRef.current.value, 10);
    if (recordId != '') {
      if (sortRef.current.value < data.length && sortRef.current.value > 0) {
        // console.log(data[swapId - 1].id, data[recordId - 1].id);
        setShowModal(false);
        const formDataToSend = new FormData();
        formDataToSend.append("sound_id_1", data[swapId - 1].id);
        formDataToSend.append("sound_id_2", data[recordId - 1].id);
        try {
          const response = await axios.put(`${BASEURL}/api/sounds/swap-sounds/`, formDataToSend);
          // console.log(response);
          setLoader(true);
          (async function () {
            try {
              const response = await fetchData(
                enteries,
                offset,
                dateFrom,
                dateTo,
                filterSearchValue,
                listDropdownValue,
                selectedLanguage
              );
              // console.log(response.data.data);
              setData(response.data.data);
              setTotalCount(response.data.count);
              setLoader(false);
            } catch (error) {
              console.error(error);
            }
          })();
        } catch (error) {
          console.log(error);
        }
      } else {
        setError({ error: true, text: 'Please Enter Correct No' });
      }
    } else {
      setError({ error: true, text: 'Please Enter No' });
    }
  }

  const modulesWisePermission = (condition) => {
    if (!permissions) {
      return 'd-none'
    }
    return disabledButtonByPermission(permissions, condition)
  }

  const handleChangeEntries = (no) => {
    setOffset(0)
    setEnteries(no)
  }

  const onSortEnd = (result) => {
    if (!result.destination) return;

    const oldIndex = result.source.index;
    const newIndex = result.destination.index;

    if (oldIndex === newIndex) return;

    const reorderedList = Array.from(data);
    const [movedItem] = reorderedList.splice(oldIndex, 1);
    reorderedList.splice(newIndex, 0, movedItem);
    setIsListPositionUpdated(true);
    setData(reorderedList)

  };


  const updateOrdering = async () => {
    try {
      questionModal
        .fire({
          title: getQuestionModalTitleForSorting(listDropdownValue, 'Sounds'),
          confirmButtonText: "Yes",
        })
        .then((result) => {
          if (result.isConfirmed) {
            setLoader(true);
            callSortingApi()
          }
        });
    } catch (error) {
      console.log(error)
    }

  }

  const callSortingApi = async () => {
    try {
      const url = listDropdownValue === 'all' ? '/api/sounds/update-sequence/' : `/api/sounds/update-featured-sequence/`;
      const result = await listSorted({
        url: url,
        data: data,
        key: 'soundid'
      })
      if (!result.data.error) {
        setLoader(false);
        successModal.fire({
          text: sortingUpdatedSuccessfullyMsg(listDropdownValue, 'Sounds'),
          confirmButtonText: "Continue",
        });
        setIsListPositionUpdated(false);
      } else {
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  }

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-lg-8 mb-2">
              <h2 className="mainTitle">Sound </h2>
            </div>
            <div className="col-lg-4 text-end mb-2">
              <button disabled={data.length > 0 && isListPositionUpdated ? false : true} className="customButton primaryButton me-3" onClick={updateOrdering}>
                Update Ordering
              </button>
              <Link
                to={"/sound-management/add-sound"}
                className={`customButton primaryButton ${modulesWisePermission('can_add')}`}
              >
                Add Sound
              </Link>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CustomFilters
                enteries={enteries}
                totalCount={totalCount}
                entriesFilter={true}
                setEnteries={handleChangeEntries}
                filterSearch={true}
                filterSearchValue={filterSearchValue}
                setFilterSearchValue={setFilterSearchValue}
                listDropdown={true}
                listDropdownValue={listDropdownValue}
                setListDropdownValue={setListDropdownValue}
                // dateFilter={true}
                setDateFrom={setDateFrom}
                setDateTo={setDateTo}
                languageFilter
                languageValue={selectedLanguage}
                setLanguageValue={setSelectedLanguage}
              />
            </div>
          </div>

          <div className="row mb-3 position-relative">
            <div className="col-12">
              <DragDropContext onDragEnd={onSortEnd}>
                <Droppable droppableId="sound-music-list">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      <CustomTable headers={soundHeader}>
                        <tbody>

                          {loader && (
                            <tr>
                              <td colSpan={15}>
                                <div className="d-flex align-items-center justify-content-center" style={{ height: 300 }}>
                                  <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </Spinner>
                                </div>
                              </td>
                            </tr>
                          )}
                          {!loader && data && data.map((item, index) => (
                            <Draggable
                              draggableId={`sound-music-${item.id}`}
                              index={index}
                              key={item.id}
                            >
                              {(provided, snapshot) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`my-1 ${snapshot.isDragging ? "dragging" : ""}`}
                                >
                                  <td>
                                    {/* <button type="button" className="btn lightColor outline-0 border-0" onClick={() => swipModal(index + 1, item.id)}>{index + 1}</button> */}
                                    {index + 1}
                                  </td>
                                  <td className="text-capitalize">
                                    <img src={item.thumbnail ? `${item.thumbnail}` : placeholderImage}
                                      alt="thumbnail"
                                      className="thumbnail"
                                    />
                                    {item.title}
                                  </td>
                                  <td>{item.premium ? "Premium" : "Free"}</td>
                                  <td><ReadOnlyDropdown options={item?.categories} /></td>
                                  <td><ReadOnlyDropdown options={item?.subcategories} /></td>
                                  <td>{item?.child_content ? "YES" : "NO"}</td>

                                  <td>
                                    <Dropdown className="tableDropdown">
                                      <Dropdown.Toggle
                                        variant="transparent"
                                        className="notButton classicToggle"
                                      >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu
                                        align="end"
                                        className="tableDropdownMenu"
                                      >
                                        <Link
                                          to={`/sound-management/sound-details/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_view')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEye}
                                            className="tableActionIcon"
                                          />
                                          View
                                        </Link>
                                        <Link
                                          to={`/sound-management/edit-sound/${item.id}`}
                                          className={`tableAction ${modulesWisePermission('can_edit')}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEdit}
                                            className="tableActionIcon"
                                          />
                                          Edit
                                        </Link>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_add')}`}
                                          onClick={() => {
                                            handleDuplicateSound(item);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faCopy}
                                            className="tableActionIcon"
                                          />
                                          Duplicate
                                        </button>
                                        <button
                                          className={`tableAction ${modulesWisePermission('can_delete')}`}
                                          onClick={() => {
                                            deleteSoundAction(item.id, item.title);
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faTimes}
                                            className="tableActionIcon"
                                          />
                                          Hide
                                        </button>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      </CustomTable>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <CustomPagination
                enteries={enteries}
                totalCount={totalCount}
                setCurrentPage={setOffset}
                currentPage={offset}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>

      {loader && <CustomLoader />}

      <Modal
        show={showModal}
        centered
        className="swap-modal"
        backdrop="static"
      >
        <Modal.Body>
          <div className="pb-5 w-100 d-flex flex-column align-items-center">
            <div className="text-end ms-auto">
              <button
                className="closeButton notButton ms-auto"
                onClick={() => { setShowModal(false); }}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className="cross-icon"
                  color="white"
                />
              </button>
            </div>
            <div className="w-100 d-flex flex-column align-items-center" style={{ maxWidth: '500px' }}>
              <div className="d-flex align-items-center mt-3 pt-3">
                <h2 className="mb-0 me-4">Record <b className="mx-2">{swapId}</b> swap with </h2>
                <input type="number" ref={sortRef} onChange={() => setError({ error: false, text: '' })} />
              </div>
              {error?.error && <small className="text-danger mt-3">{error.text}</small>}
            </div>
            <div className="mt-3 pt-3 d-flex gap-3 justify-content-center">
              <button style={{ backgroundColor: '#ffffff1f' }} className='px-5 py-2 fs-6 fw-light text-white text-capitalize btn rounded-pill d-flex align-items-center justify-content-center flex-shrink-0' onClick={setSorting}>
                <span className=''>Update</span>
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

    </>
  );
};
export default SoundManagement;
